import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FilterIcon, UserPlusIcon, MegaphoneIcon, ArrowRightIcon, CheckIcon, XIcon } from 'lucide-react';
const tabs = [{
  id: 'build',
  icon: FilterIcon,
  title: 'Build precise local audiences',
  description: 'Filter 200M+ local businesses by location, category, revenue, employee count, and tech stack to find your exact ideal customer profile.'
}, {
  id: 'enrich',
  icon: UserPlusIcon,
  title: 'Enrich with verified contact data',
  description: 'Get 50+ data fields per local business, including verified owner emails, phone numbers, and social media profiles ready for outreach.'
}, {
  id: 'activate',
  icon: MegaphoneIcon,
  title: 'Activate on ad platforms',
  description: 'Upload your enriched local business contacts to ad platforms like Meta Ads as custom audiences for highly targeted local campaigns.'
}];
export function AudienceBuilder() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-4684">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4685">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4686">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4687">
            Everything you need for local business{' '}
            <span className="text-blue-600" data-id="element-4688">audience targeting</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4689">
            Stop wasting ad spend on broad targeting. Use deterministic local
            business data to reach exactly who you want.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-4690">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-4691">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-4692">
                  <div className="flex items-start gap-6" data-id="element-4693">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-4694">
                      <tab.icon className="w-6 h-6" data-id="element-4695" />
                    </div>
                    <div data-id="element-4696">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-4697">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-4698">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-4699">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-4700">
              <AnimatePresence mode="wait" data-id="element-4701">
                {activeTab === 'build' && <motion.div key="build" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-4702">
                    <div className="flex items-center justify-between mb-6" data-id="element-4703">
                      <h4 className="font-bold text-slate-900" data-id="element-4704">
                        Audience filters
                      </h4>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full" data-id="element-4705">
                        2,847 matches
                      </span>
                    </div>
                    <div className="space-y-5" data-id="element-4706">
                      <div data-id="element-4707">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4708">
                          Location
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100 flex items-center justify-between" data-id="element-4709">
                          <span data-id="element-4710">Miami, FL (50 miles)</span>
                          <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-4711" />
                        </div>
                      </div>
                      <div data-id="element-4712">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4713">
                          Categories
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-4714">
                          <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg flex items-center gap-1.5 border border-blue-100" data-id="element-4715">
                            Restaurants <XIcon className="w-3 h-3" data-id="element-4716" />
                          </span>
                          <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg flex items-center gap-1.5 border border-blue-100" data-id="element-4717">
                            Cafes <XIcon className="w-3 h-3" data-id="element-4718" />
                          </span>
                          <span className="px-3 py-1.5 bg-slate-50 text-slate-500 text-sm font-medium rounded-lg border border-slate-200 border-dashed" data-id="element-4719">
                            + Add category
                          </span>
                        </div>
                      </div>
                      <div data-id="element-4720">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4721">
                          Estimated revenue
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-4722">
                          $500k - $2M
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-4723">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-4724">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4725">
                        Before enrichment
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-4726" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-4727">
                        After Openmart
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-4728">
                      {/* Before card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-4" data-id="element-4729">
                        <div className="flex items-center gap-3 mb-2" data-id="element-4730">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-sm font-bold" data-id="element-4731">
                            SB
                          </div>
                          <div data-id="element-4732">
                            <div className="text-sm font-bold text-slate-900" data-id="element-4733">
                              Sunrise bakery
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-4734">
                              456 Ocean Dr, Miami
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-4735">
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-4736">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-4737" />
                            <span data-id="element-4738">No email available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-4739">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-4740" />
                            <span data-id="element-4741">No phone available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-4742">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-4743" />
                            <span data-id="element-4744">No owner info</span>
                          </div>
                        </div>
                      </div>

                      {/* After card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-blue-100 space-y-4" data-id="element-4745">
                        <div className="flex items-center gap-3 mb-2" data-id="element-4746">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold" data-id="element-4747">
                            SB
                          </div>
                          <div data-id="element-4748">
                            <div className="text-sm font-bold text-slate-900" data-id="element-4749">
                              Sunrise bakery
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-4750">
                              456 Ocean Dr, Miami
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-4751">
                          <div className="flex items-center gap-2 text-xs" data-id="element-4752">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4753" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-4754">
                              sarah@sunrisebakery.com
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-4755">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4756" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-4757">
                              (305) 555-0199
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-4758">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4759" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-4760">
                              Sarah Jenkins, Owner
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-4761">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4762" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-4763">
                              @sunrisebakery
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'activate' && <motion.div key="activate" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-4764">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-4765">
                      <div className="flex justify-center items-center gap-4 mb-8" data-id="element-4766">
                        <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm" data-id="element-4767">
                          <span className="font-bold text-xl text-slate-900" data-id="element-4768">
                            CSV
                          </span>
                        </div>
                        <div className="flex flex-col items-center" data-id="element-4769">
                          <ArrowRightIcon className="w-6 h-6 text-blue-400 mb-1" data-id="element-4770" />
                          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-4771">
                            Upload
                          </span>
                        </div>
                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm" data-id="element-4772">
                          <MegaphoneIcon className="w-8 h-8 text-blue-600" data-id="element-4773" />
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-5 text-left border border-slate-100" data-id="element-4774">
                        <div className="flex items-center gap-3 mb-4" data-id="element-4775">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-4776">
                            <CheckIcon className="w-5 h-5 text-green-600" data-id="element-4777" />
                          </div>
                          <div data-id="element-4778">
                            <h4 className="font-bold text-slate-900 text-sm" data-id="element-4779">
                              Custom audience created
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-4780">
                              Ready for ad delivery
                            </p>
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-4781">
                          <div data-id="element-4782">
                            <div className="flex justify-between text-xs font-semibold mb-1.5" data-id="element-4783">
                              <span className="text-slate-600" data-id="element-4784">Match rate</span>
                              <span className="text-slate-900" data-id="element-4785">84%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-4786">
                              <div className="h-full bg-blue-500 rounded-full w-[84%]" data-id="element-4787" />
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 text-center pt-2" data-id="element-4788">
                            2,391 local business contacts matched
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
// Simple ChevronDownIcon for the dropdown
function ChevronDownIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4789">
      <path d="m6 9 6 6 6-6" data-id="element-4790" />
    </svg>;
}